import React, { useCallback, useRef, useState } from 'react'
import { Box, MenuItem, MenuList, Popover } from '@mui/material'

import { type LicenseStatus } from '~types'

import {
  LicenseStatusChip,
  StatusChip,
  type StatusChipConfig,
  type StatusChipType,
  caseStatusConfig,
  licenseStatusConfig,
} from '../Chip'
import Icon from '../Icon'

import './StatusSelector.scss'

interface StatusSelectorBaseProps {
  disabled?: boolean
  id?: string
}

export interface CaseStatusSelectorProps extends StatusSelectorBaseProps {
  statusType?: 'case'
  value?: StatusChipType
  onChange?: (status: StatusChipType) => void
  statuses?: StatusChipType[]
}

export interface LicenseStatusSelectorProps extends StatusSelectorBaseProps {
  statusType: 'license'
  value?: LicenseStatus
  onChange?: (status: LicenseStatus) => void
  statuses?: LicenseStatus[]
}

export type StatusSelectorProps = CaseStatusSelectorProps | LicenseStatusSelectorProps

type SelectableStatus = StatusChipType | LicenseStatus

const StatusSelector: React.FC<StatusSelectorProps> = (props): React.ReactElement => {
  const { statusType = 'case', value: valueProp, disabled = false, id } = props

  const configs: Partial<Record<SelectableStatus, StatusChipConfig>> =
    statusType === 'license' ? licenseStatusConfig : caseStatusConfig
  const statuses = props.statuses ?? (Object.keys(configs) as SelectableStatus[])
  const value = valueProp ?? statuses[0]
  const onChange = props.onChange as ((status: SelectableStatus) => void) | undefined

  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)

  const handleOpen = useCallback(() => {
    if (disabled) return
    setOpen(true)
  }, [disabled])
  const handleClose = useCallback(() => setOpen(false), [])

  const handleSelect = useCallback(
    (status: SelectableStatus) => {
      onChange?.(status)
      handleClose()
    },
    [onChange, handleClose],
  )

  const renderChip = (
    status: SelectableStatus,
    chipProps?: { deleteIcon: React.ReactElement; onDelete: () => void },
  ): React.ReactElement | null => {
    const config = configs[status]
    if (!config) return null
    if (statusType === 'license') {
      return <LicenseStatusChip status={status as LicenseStatus} {...chipProps} />
    }
    return <StatusChip config={config} {...chipProps} />
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    },
    [disabled],
  )

  return (
    <>
      <div
        ref={triggerRef}
        className={`status-selector__trigger${disabled ? ' status-selector__trigger--disabled' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          handleOpen()
        }}
        onKeyDown={handleKeyDown}
        role='button'
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        id={id}
      >
        {renderChip(
          value,
          !disabled
            ? {
                deleteIcon: <Icon name={open ? 'ArrowDropUp' : 'ArrowDropDown'} size='0.875rem' />,
                onDelete: handleOpen,
              }
            : undefined,
        )}
      </div>
      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { className: 'status-selector__paper' },
        }}
      >
        <MenuList disablePadding className='status-selector__menu'>
          {statuses.map((status) => (
            <MenuItem
              key={status}
              onClick={() => handleSelect(status)}
              selected={status === value}
              className='status-selector__option'
            >
              <Box className='status-selector__option-row'>
                {renderChip(status)}
                {status === value && <Icon name='Check' className='status-selector__check' />}
              </Box>
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  )
}

export default StatusSelector
